// packages/web/src/services/homeworkApi.ts
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// 请求拦截器：添加 token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：统一错误处理
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface Homework {
  id: string;
  name: string;
  description?: string;
  subject: string;
  due_date: string;
  status: string;
  created_at: string;
  class_count: number;
  total_students: number;
  completed_students: number;
}

export interface HomeworkDetail extends Homework {
  exercises: any[];
  assignments: Array<{
    assignee_type: 'student' | 'class';
    assignee_id: string;
    assignee_name: string;
  }>;
}

export interface StudentProgress {
  student_id: string;
  student_name: string;
  class_name?: string;
  completed_exercises: number;
  total_exercises: number;
  completion_percentage: number;
  status: 'not_started' | 'in_progress' | 'completed' | 'overdue';
  last_activity?: string;
}

export interface CreateHomeworkData {
  name: string;
  description?: string;
  dueDate: string;
  exerciseIds: string[];
  assignments: Array<{ type: 'student' | 'class'; id: string }>;
}

export const homeworkApi = {
  /**
   * 获取作业列表
   */
  async getList(filters?: any): Promise<{ success: boolean; data: { homeworks: Homework[] } }> {
    const response = await apiClient.get('/teach/homeworks', { params: filters });
    return response.data;
  },

  /**
   * 获取作业详情
   */
  async getDetail(homeworkId: string): Promise<{ success: boolean; data: HomeworkDetail }> {
    const response = await apiClient.get(`/teach/homeworks/${homeworkId}`);
    return response.data;
  },

  /**
   * 创建作业
   */
  async create(data: CreateHomeworkData): Promise<{ success: boolean; data: Homework }> {
    const response = await apiClient.post('/teach/homeworks', data);
    return response.data;
  },

  /**
   * 更新作业
   */
  async update(
    homeworkId: string,
    updates: { name?: string; description?: string; due_date?: string }
  ): Promise<{ success: boolean; data: Homework }> {
    const response = await apiClient.put(`/teach/homeworks/${homeworkId}`, updates);
    return response.data;
  },

  /**
   * 删除作业
   */
  async delete(homeworkId: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete(`/teach/homeworks/${homeworkId}`);
    return response.data;
  },

  /**
   * 获取作业完成情况
   */
  async getProgress(homeworkId: string): Promise<{
    success: boolean;
    data: {
      homework_id: string;
      total_students: number;
      completed_students: number;
      completion_rate: number;
      student_progress: StudentProgress[];
    };
  }> {
    const response = await apiClient.get(`/teach/homeworks/${homeworkId}/progress`);
    return response.data;
  },

  /**
   * 添加习题到作业
   */
  async addExercises(homeworkId: string, exerciseIds: string[]): Promise<{ success: boolean }> {
    const response = await apiClient.post(`/teach/homeworks/${homeworkId}/exercises`, {
      exerciseIds,
    });
    return response.data;
  },
};
